import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar, MapPin, Clock, Users, Search, Filter, Plus, ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import EventSubmitDialog from "@/components/EventSubmitDialog";

// Sample events data (fallback if API has no events)
const SAMPLE_EVENTS = [
  {
    event_id: "evt_1",
    title: "Black Business Networking Night",
    description: "Connect with fellow Black entrepreneurs, share experiences, and build lasting business relationships.",
    date: "2025-01-25",
    time: "6:00 PM - 9:00 PM",
    location: "Atlanta, GA",
    venue: "The Gathering Spot",
    category: "Networking",
    image: "https://images.unsplash.com/photo-1739298061709-cfc57e6b620e?w=600&q=80",
    attendees: 85,
    price: "Free"
  },
  {
    event_id: "evt_2",
    title: "Women in Business Summit",
    description: "A full-day summit celebrating and empowering Black women entrepreneurs with workshops and panels.",
    date: "2025-02-14",
    time: "9:00 AM - 5:00 PM",
    location: "Houston, TX",
    venue: "Houston Marriott Marquis",
    category: "Conference",
    image: "https://images.unsplash.com/photo-1573165706511-3ffde6ef1fe3?w=600&q=80",
    attendees: 250,
    price: "$75"
  },
  {
    event_id: "evt_3",
    title: "Tech Founders Meetup",
    description: "Monthly meetup for Black tech founders to discuss trends, challenges, and opportunities in tech.",
    date: "2025-01-30",
    time: "7:00 PM - 9:00 PM",
    location: "San Francisco, CA",
    venue: "WeWork Market Street",
    category: "Tech",
    image: "https://images.unsplash.com/photo-1689857538296-b6e1a392a91d?w=600&q=80",
    attendees: 45,
    price: "Free"
  },
  {
    event_id: "evt_4",
    title: "Culinary Arts Workshop",
    description: "Learn from renowned Black chefs in this hands-on cooking workshop featuring soul food classics.",
    date: "2025-02-08",
    time: "2:00 PM - 5:00 PM",
    location: "Chicago, IL",
    venue: "Kendall College",
    category: "Workshop",
    image: "https://images.unsplash.com/photo-1592498542930-4254dc73cbb5?w=600&q=80",
    attendees: 30,
    price: "$50"
  },
  {
    event_id: "evt_5",
    title: "Real Estate Investment Seminar",
    description: "Expert insights on building wealth through real estate investment in minority communities.",
    date: "2025-02-20",
    time: "10:00 AM - 2:00 PM",
    location: "Minneapolis, MN",
    venue: "Capella Tower",
    category: "Education",
    image: "https://images.unsplash.com/photo-1621973133111-63649acea55a?w=600&q=80",
    attendees: 120,
    price: "$25"
  },
  {
    event_id: "evt_6",
    title: "Fashion Pop-Up Market",
    description: "Shop exclusive collections from Black fashion designers and discover emerging talent.",
    date: "2025-03-01",
    time: "11:00 AM - 7:00 PM",
    location: "New York, NY",
    venue: "Brooklyn Expo Center",
    category: "Market",
    image: "https://images.unsplash.com/photo-1761370571873-5d869310d731?w=600&q=80",
    attendees: 500,
    price: "$10"
  }
];

const EVENT_CATEGORIES = ["All", "Networking", "Conference", "Tech", "Workshop", "Education", "Market"];

export default function EventsPage() {
  const { user, login } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [locationFilter, setLocationFilter] = useState("");
  const [submitOpen, setSubmitOpen] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      const apiEvents = response.data.events || [];
      // Use API events if available, otherwise use sample events
      setEvents(apiEvents.length > 0 ? apiEvents : SAMPLE_EVENTS);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error("Error fetching events:", error);
      setEvents(SAMPLE_EVENTS);
    } finally {
      setLoading(false);
    }
  };

  const filteredEvents = events.filter(event => {
    const matchesSearch = event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          event.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "All" || event.category === selectedCategory;
    const matchesLocation = !locationFilter || 
                            event.location.toLowerCase().includes(locationFilter.toLowerCase());
    return matchesSearch && matchesCategory && matchesLocation;
  });

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="bg-tbc-navy py-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-r from-tbc-gold to-tbc-coral"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="inline-block px-4 py-2 bg-tbc-coral/20 text-tbc-coral font-medium text-sm tracking-wide rounded-full mb-4">
              COMMUNITY EVENTS
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl text-white mb-4">
              Upcoming Events
            </h1>
            <p className="text-gray-300 font-body text-lg max-w-2xl mx-auto">
              Connect, learn, and grow with the Black business community. Join networking events, workshops, and conferences.
            </p>
          </div>

          {/* Search Bar */}
          <div className="mt-8 max-w-3xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search events..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-12 rounded-full bg-white"
                  data-testid="events-search"
                />
              </div>
              <div className="relative">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Location..."
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  className="pl-12 h-12 rounded-full bg-white sm:w-48"
                  data-testid="events-location"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-6 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            {EVENT_CATEGORIES.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full font-body text-sm whitespace-nowrap transition-colors ${
                  selectedCategory === category
                    ? "bg-tbc-navy text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
                data-testid={`category-${category.toLowerCase()}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Events Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <p className="text-gray-600 font-body">
              {filteredEvents.length} {filteredEvents.length === 1 ? "event" : "events"} found
            </p>
            <Button
              className="bg-tbc-coral hover:bg-tbc-coral/90 text-white rounded-full"
              onClick={() => setSubmitOpen(true)}
              data-testid="submit-event"
            >
              <Plus className="w-4 h-4 mr-2" />
              Submit Event
            </Button>
          </div>

          {filteredEvents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredEvents.map((event) => (
                <div
                  key={event.event_id}
                  className="group bg-white rounded-xl overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow"
                  data-testid={`event-${event.event_id}`}
                >
                  <div className="aspect-video relative overflow-hidden">
                    <img
                      src={event.image}
                      alt={event.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-tbc-coral text-white text-xs font-medium rounded-full">
                        {event.category}
                      </span>
                    </div>
                    <div className="absolute top-4 right-4">
                      <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-tbc-navy text-sm font-bold rounded-full">
                        {event.price}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading text-xl text-tbc-navy mb-2 group-hover:text-tbc-coral transition-colors">
                      {event.title}
                    </h3>
                    <p className="text-gray-600 text-sm font-body line-clamp-2 mb-4">
                      {event.description}
                    </p>
                    <div className="space-y-2 text-sm text-gray-500 font-body">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-tbc-gold" />
                        {formatDate(event.date)}
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-tbc-gold" />
                        {event.time}
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-tbc-gold" />
                        {event.location}
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-tbc-gold" />
                        {event.attendees} attending
                      </div>
                    </div>
                    <Button
                      className="w-full mt-4 bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full"
                      data-testid={`register-${event.event_id}`}
                    >
                      Register Now
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="font-heading text-xl text-tbc-navy mb-2">No events found</h3>
              <p className="text-gray-500 font-body">Try adjusting your search or filters</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-tbc-cream">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl text-tbc-navy mb-4">
            Hosting an Event?
          </h2>
          <p className="text-gray-600 font-body mb-6">
            Share your event with the Black business community. Submit your networking event, workshop, or conference.
          </p>
          <Button
            className="bg-tbc-coral hover:bg-tbc-coral/90 text-white rounded-full px-8 py-6"
            onClick={() => setSubmitOpen(true)}
            data-testid="submit-event-cta"
          >
            Submit Your Event
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      <EventSubmitDialog open={submitOpen} onOpenChange={setSubmitOpen} />

      <Footer />
    </div>
  );
}
